'use client';

import { useCallback } from 'react';
import { preload } from 'react-dom';
import { Option } from 'effect';

import { toImageTemplate } from '@vs-shop/engine/image/ImageTemplate';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';
import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';
import type { ProductAgeRestriction } from '@vs-shop/schemas/branded/ProductSchema';
import type {
  CartLinePreview as CartLinePreviewT,
  CartLineRequest,
} from '@vs-shop/engine/module/cart/addToCart';

export type CartLinePreview = CartLinePreviewT;

export function createCartLinePreview(
  imagePath: string | null,
  ageRestriction: ProductAgeRestriction
): CartLinePreview | undefined {
  const imageUrl = Option.match(toImageTemplate(imagePath), {
    onNone: () => undefined,
    onSome: (template) => resolveImageUrl(template, ImageFilter.product_card_cart),
  });
  const ageRestricted = ageRestriction === '18+';

  if (imageUrl === undefined && !ageRestricted) return undefined;

  return {
    ...(imageUrl === undefined ? {} : { imageUrl }),
    ...(ageRestricted ? { ageRestricted: true } : {}),
  };
}

export function withCartLinePreview<P>(
  request: CartLineRequest<P>,
  imagePath: string | null,
  ageRestriction: ProductAgeRestriction
): CartLineRequest<P> {
  const preview = createCartLinePreview(imagePath, ageRestriction);
  return preview === undefined ? request : { ...request, preview };
}

export function useCartLinePreviewPreload(previews: readonly (CartLinePreview | undefined)[]) {
  const preloadDrawerImages = useCallback(() => {
    for (const preview of previews) {
      if (preview?.imageUrl !== undefined) {
        preload(preview.imageUrl, { as: 'image' });
      }
    }
  }, [previews]);

  return {
    onPointerEnter: preloadDrawerImages,
    onFocus: preloadDrawerImages,
  };
}
